import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  disabled,
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed select-none';

  const variants: Record<string, string> = {
    primary: [
      'bg-gradient-to-r from-[#216974] to-[#54c4b6]',
      'text-white rounded-2xl',
      'shadow-[0_2px_16px_rgba(33,105,116,0.35)]',
      'hover:shadow-[0_4px_24px_rgba(33,105,116,0.5)]',
      'hover:from-[#1a5560] hover:to-[#3db0a2]',
      'active:scale-[0.98]',
      'focus:ring-[#54c4b6]/40',
      'border border-transparent',
    ].join(' '),
    secondary: [
      'bg-white/80 backdrop-blur-sm',
      'text-gray-700 rounded-2xl',
      'border border-gray-200/80',
      'shadow-sm hover:shadow-md',
      'hover:bg-white hover:border-gray-300',
      'active:scale-[0.98]',
      'focus:ring-gray-300/60',
    ].join(' '),
    outline: [
      'bg-transparent',
      'text-[#216974] rounded-2xl',
      'border-2 border-[#54c4b6]/60',
      'hover:bg-[#216974] hover:text-white hover:border-[#216974]',
      'active:scale-[0.98]',
      'focus:ring-[#54c4b6]/30',
    ].join(' '),
    ghost: [
      'bg-transparent',
      'text-gray-600 rounded-xl',
      'hover:bg-gray-100/80 hover:text-gray-900',
      'active:scale-[0.97]',
      'focus:ring-gray-200',
    ].join(' '),
    danger: [
      'bg-gradient-to-r from-[#216974] to-[#216974]',
      'text-white rounded-2xl',
      'shadow-[0_2px_16px_rgba(33,105,116,0.35)]',
      'hover:shadow-[0_4px_24px_rgba(33,105,116,0.5)]',
      'active:scale-[0.98]',
      'focus:ring-[#216974]/40',
      'border border-transparent',
    ].join(' '),
    success: [
      'bg-gradient-to-r from-[#54c4b6] to-[#216974]',
      'text-white rounded-2xl',
      'shadow-[0_2px_16px_rgba(84,196,182,0.35)]',
      'hover:shadow-[0_4px_24px_rgba(84,196,182,0.5)]',
      'active:scale-[0.98]',
      'focus:ring-[#54c4b6]/40',
      'border border-transparent',
    ].join(' '),
  };

  const sizes: Record<string, string> = {
    xs:  'h-7  px-3   text-xs   gap-1.5',
    sm:  'h-9  px-4   text-sm   gap-2',
    md:  'h-10 px-5   text-sm   gap-2',
    lg:  'h-12 px-6   text-base gap-2.5',
    xl:  'h-14 px-8   text-lg   gap-3',
  };

  return (
    <motion.button
      whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
      whileHover={{ scale: disabled || isLoading ? 1 : 1.005 }}
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...(props as HTMLMotionProps<'button'>)}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
      ) : (
        leftIcon && <span className="shrink-0">{leftIcon}</span>
      )}
      <span>{children}</span>
      {!isLoading && rightIcon && <span className="shrink-0">{rightIcon}</span>}
    </motion.button>
  );
};

export default Button;
